/**
 * Execution Store
 *
 * Reactive SolidJS store for managing execution state and events.
 * Features:
 * - Real-time event accumulation via WebSocket
 * - Derived state (phase, progress, stats)
 * - Agent filtering for multi-agent executions
 * - Event categorization and timeline
 */

import { createSignal, createMemo, onCleanup, Accessor } from 'solid-js';
import { websocketService, type ConnectionState } from '../services/websocket.ts';
import type { ExecutionEvent, ExecutionEventType, Execution } from '../services/api.ts';
import { fetchExecutionEvents } from '../services/api.ts';

// ============================================================================
// Types
// ============================================================================

export type ExecutionPhase =
  | 'initializing'
  | 'thinking'
  | 'planning'
  | 'executing'
  | 'completing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface ExecutionProgress {
  phase: ExecutionPhase;
  currentStep?: string;
  currentStepIndex?: number;
  completedSteps: number;
  totalSteps: number;
  percentage: number;
}

export interface PlanStep {
  id: string;
  description: string;
  toolName?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped';
}

export interface ExecutionStats {
  totalEvents: number;
  thinkingCount: number;
  toolCallCount: number;
  llmCallCount: number;
  totalDurationMs: number;
  totalTokens: number;
  errorCount: number;
  models: string[];
}

export interface AgentInfo {
  id: string;
  name?: string;
  eventCount: number;
}

export interface ExecutionStoreState {
  executionId: string | null;
  execution: Execution | null;
  events: ExecutionEvent[];
  connectionState: ConnectionState;
  agentIdFilter: string | null;
  isLoading: boolean;
  error: string | null;
}

// ============================================================================
// Event Category Helpers
// ============================================================================

export function getEventCategory(eventType: ExecutionEventType): string {
  switch (eventType) {
    case 'execution_started':
    case 'execution_completed':
    case 'execution_failed':
    case 'execution_cancelled':
      return 'execution';
    case 'thinking_started':
    case 'thinking_completed':
      return 'thinking';
    case 'planning_started':
    case 'planning_completed':
      return 'planning';
    case 'step_started':
    case 'step_completed':
    case 'step_failed':
      return 'step';
    case 'tool_call_started':
    case 'tool_call_completed':
    case 'tool_call_failed':
      return 'tool';
    case 'llm_call_started':
    case 'llm_call_completed':
      return 'llm';
    case 'agent_message':
      return 'message';
    case 'progress_update':
      return 'progress';
    default:
      return 'other';
  }
}

export function getEventLabel(eventType: ExecutionEventType): string {
  const labels: Record<ExecutionEventType, string> = {
    execution_started: 'Execution Started',
    execution_completed: 'Execution Completed',
    execution_failed: 'Execution Failed',
    execution_cancelled: 'Execution Cancelled',
    thinking_started: 'Thinking',
    thinking_completed: 'Thought Complete',
    planning_started: 'Planning',
    planning_completed: 'Plan Created',
    step_started: 'Step Started',
    step_completed: 'Step Complete',
    step_failed: 'Step Failed',
    tool_call_started: 'Tool Call',
    tool_call_completed: 'Tool Complete',
    tool_call_failed: 'Tool Failed',
    llm_call_started: 'LLM Call',
    llm_call_completed: 'LLM Response',
    agent_message: 'Message',
    progress_update: 'Progress',
  };
  return labels[eventType] || eventType;
}

export function getEventColor(eventType: ExecutionEventType): string {
  const category = getEventCategory(eventType);
  const colors: Record<string, string> = {
    execution: 'purple',
    thinking: 'yellow',
    planning: 'blue',
    step: 'cyan',
    tool: 'orange',
    llm: 'green',
    message: 'gray',
    progress: 'gray',
    other: 'gray',
  };
  return colors[category] || 'gray';
}

// ============================================================================
// Create Execution Store Hook
// ============================================================================

export interface ExecutionStore {
  // State accessors
  state: {
    executionId: Accessor<string | null>;
    execution: Accessor<Execution | null>;
    events: Accessor<ExecutionEvent[]>;
    connectionState: Accessor<ConnectionState>;
    agentIdFilter: Accessor<string | null>;
    isLoading: Accessor<boolean>;
    error: Accessor<string | null>;
  };
  // Derived state
  filteredEvents: Accessor<ExecutionEvent[]>;
  phase: Accessor<ExecutionPhase>;
  progress: Accessor<ExecutionProgress>;
  planSteps: Accessor<PlanStep[]>;
  stats: Accessor<ExecutionStats>;
  agents: Accessor<AgentInfo[]>;
  latestThinking: Accessor<string | null>;
  latestMessage: Accessor<{ level: string; message: string } | null>;
  // Actions
  subscribe: (executionId: string, execution?: Execution) => void;
  unsubscribe: () => void;
  setAgentFilter: (agentId: string | null) => void;
  clearError: () => void;
  refreshEvents: () => Promise<void>;
}

export function createExecutionStore(): ExecutionStore {
  // Core state
  const [executionId, setExecutionId] = createSignal<string | null>(null);
  const [execution, setExecution] = createSignal<Execution | null>(null);
  const [events, setEvents] = createSignal<ExecutionEvent[]>([]);
  const [connectionState, setConnectionState] = createSignal<ConnectionState>('disconnected');
  const [agentIdFilter, setAgentIdFilter] = createSignal<string | null>(null);
  const [isLoading, setIsLoading] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // Subscription cleanup
  let unsubscribeWs: (() => void) | null = null;
  let unsubscribeConnectionState: (() => void) | null = null;

  // ============================================================================
  // Derived State
  // ============================================================================

  // Filtered events based on agent filter
  const filteredEvents = createMemo(() => {
    const filter = agentIdFilter();
    const allEvents = events();

    if (!filter) return allEvents;
    return allEvents.filter((e) => e.agentId === filter || !e.agentId);
  });

  // Current execution phase
  const phase = createMemo<ExecutionPhase>(() => {
    const evts = events();
    const exec = execution();

    // If no events, fall back to execution status
    if (evts.length === 0) {
      if (exec?.status) {
        // Map execution status to phase
        const statusToPhase: Record<string, ExecutionPhase> = {
          pending: 'initializing',
          running: 'executing',
          completed: 'completed',
          failed: 'failed',
          cancelled: 'cancelled',
        };
        return statusToPhase[exec.status] || 'initializing';
      }
      return 'initializing';
    }

    // Check for terminal states first
    const lastEvent = evts[evts.length - 1];
    if (lastEvent.eventType === 'execution_completed') return 'completed';
    if (lastEvent.eventType === 'execution_failed') return 'failed';
    if (lastEvent.eventType === 'execution_cancelled') return 'cancelled';

    // Determine current phase from events
    const hasThinking = evts.some((e) => e.eventType === 'thinking_started');
    const hasPlanning = evts.some((e) => e.eventType === 'planning_started');
    const planningComplete = evts.some((e) => e.eventType === 'planning_completed');
    const hasSteps = evts.some((e) => e.eventType === 'step_started');

    if (hasSteps || planningComplete) return 'executing';
    if (hasPlanning && !planningComplete) return 'planning';
    if (hasThinking) return 'thinking';

    return 'initializing';
  });

  // Plan steps extracted from planning_completed event
  const planSteps = createMemo<PlanStep[]>(() => {
    const evts = events();
    const planningEvent = evts.find((e) => e.eventType === 'planning_completed');
    if (!planningEvent) return [];

    const payload = planningEvent.payload as { steps?: PlanStep[] };
    if (!payload?.steps) return [];

    // Update step statuses based on subsequent events
    const stepMap = new Map(payload.steps.map((s) => [s.id, { ...s }]));

    evts.forEach((e) => {
      if (e.eventType === 'step_started') {
        const stepPayload = e.payload as { stepId?: string };
        if (stepPayload?.stepId && stepMap.has(stepPayload.stepId)) {
          stepMap.get(stepPayload.stepId)!.status = 'in_progress';
        }
      } else if (e.eventType === 'step_completed') {
        const stepPayload = e.payload as { stepId?: string };
        if (stepPayload?.stepId && stepMap.has(stepPayload.stepId)) {
          stepMap.get(stepPayload.stepId)!.status = 'completed';
        }
      } else if (e.eventType === 'step_failed') {
        const stepPayload = e.payload as { stepId?: string };
        if (stepPayload?.stepId && stepMap.has(stepPayload.stepId)) {
          stepMap.get(stepPayload.stepId)!.status = 'failed';
        }
      }
    });

    return Array.from(stepMap.values());
  });

  // Execution progress
  const progress = createMemo<ExecutionProgress>(() => {
    const steps = planSteps();
    const currentPhase = phase();

    const completedSteps = steps.filter((s) => s.status === 'completed').length;
    const currentStep = steps.find((s) => s.status === 'in_progress');
    const totalSteps = steps.length || 1;
    const percentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

    return {
      phase: currentPhase,
      currentStep: currentStep?.description,
      currentStepIndex: currentStep ? steps.indexOf(currentStep) : undefined,
      completedSteps,
      totalSteps,
      percentage: currentPhase === 'completed' ? 100 : percentage,
    };
  });

  // Execution statistics
  const stats = createMemo<ExecutionStats>(() => {
    const evts = events();
    let totalTokens = 0;
    let totalDurationMs = 0;
    const modelsSet = new Set<string>();

    evts.forEach((e) => {
      if (e.durationMs) totalDurationMs += e.durationMs;

      if (e.eventType === 'llm_call_completed' || e.eventType === 'llm_call_started') {
        const payload = e.payload as { totalTokens?: number; model?: string };
        if (payload?.totalTokens) totalTokens += payload.totalTokens;
        if (payload?.model) modelsSet.add(payload.model);
      }
    });

    return {
      totalEvents: evts.length,
      thinkingCount: evts.filter((e) => e.eventType === 'thinking_completed').length,
      toolCallCount: evts.filter((e) => e.eventType === 'tool_call_completed' || e.eventType === 'tool_call_failed').length,
      llmCallCount: evts.filter((e) => e.eventType === 'llm_call_completed').length,
      totalDurationMs,
      totalTokens,
      errorCount: evts.filter((e) => e.eventType.includes('failed')).length,
      models: Array.from(modelsSet),
    };
  });

  // Unique agents involved in execution
  const agents = createMemo<AgentInfo[]>(() => {
    const evts = events();
    const agentMap = new Map<string, AgentInfo>();

    evts.forEach((e) => {
      if (e.agentId) {
        const existing = agentMap.get(e.agentId);
        if (existing) {
          existing.eventCount++;
        } else {
          agentMap.set(e.agentId, {
            id: e.agentId,
            name: e.agentName,
            eventCount: 1,
          });
        }
      }
    });

    return Array.from(agentMap.values());
  });

  // Latest thinking content
  const latestThinking = createMemo<string | null>(() => {
    const evts = events();
    const thinkingEvents = evts.filter((e) => e.eventType === 'thinking_completed');
    if (thinkingEvents.length === 0) return null;

    const lastThinking = thinkingEvents[thinkingEvents.length - 1];
    const payload = lastThinking.payload as { content?: string };
    return payload?.content || null;
  });

  // Latest agent message
  const latestMessage = createMemo<{ level: string; message: string } | null>(() => {
    const evts = events();
    const messageEvents = evts.filter((e) => e.eventType === 'agent_message');
    if (messageEvents.length === 0) return null;

    const lastMessage = messageEvents[messageEvents.length - 1];
    const payload = lastMessage.payload as { level?: string; message?: string };
    return payload?.message ? { level: payload.level || 'info', message: payload.message } : null;
  });

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Load events from HTTP API (fallback when WebSocket unavailable)
   */
  const loadEventsFromApi = async (id: string): Promise<void> => {
    try {
      const result = await fetchExecutionEvents(id, { limit: 1000 });
      setEvents((prev) => {
        // Merge API events with any existing events, avoiding duplicates
        const existing = new Set(prev.map((e) => e.sequenceNumber));
        const newEvents = result.items.filter((e) => !existing.has(e.sequenceNumber));
        if (newEvents.length === 0) return prev;
        return [...prev, ...newEvents].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
      });
      setIsLoading(false);
    } catch (err) {
      console.error('[ExecutionStore] Failed to load events from API:', err);
      setError(err instanceof Error ? err.message : 'Failed to load events');
      setIsLoading(false);
    }
  };

  const subscribe = (id: string, exec?: Execution) => {
    // If already subscribed to this execution, just update the execution object
    // This prevents clearing events when the execution resource refetches
    const currentId = executionId();
    if (currentId === id) {
      // Update execution data without clearing events
      if (exec) {
        setExecution(exec);
        // If execution is now terminal and we have WebSocket, clean it up
        const isTerminal = ['completed', 'failed', 'cancelled'].includes(exec.status);
        if (isTerminal && unsubscribeWs) {
          unsubscribeWs();
          unsubscribeWs = null;
          setConnectionState('disconnected');
        }
      }
      return;
    }

    // Cleanup existing subscription for different execution
    unsubscribe();

    setExecutionId(id);
    setExecution(exec || null);
    setEvents([]);
    setError(null);
    setIsLoading(true);

    // Always try to load historical events via HTTP API first
    // This provides immediate data even if WebSocket fails
    loadEventsFromApi(id);

    // Check if execution is in terminal state - use HTTP API only
    // If exec is not provided yet, assume it might be live and set up WebSocket
    const isTerminal = exec && ['completed', 'failed', 'cancelled'].includes(exec.status);

    // For terminal executions, don't bother with WebSocket
    if (isTerminal) {
      setConnectionState('disconnected');
      return;
    }

    // Subscribe to connection state
    unsubscribeConnectionState = websocketService.onConnectionStateChange((state) => {
      setConnectionState(state);
      // If WebSocket fails after multiple attempts, we already have HTTP data
      if (state === 'disconnected') {
        console.log('[ExecutionStore] WebSocket disconnected, using HTTP fallback');
      }
    });

    // Subscribe to execution events for real-time updates
    unsubscribeWs = websocketService.subscribeToExecution(
      id,
      (event) => {
        setEvents((prev) => {
          // Avoid duplicates based on sequence number
          if (prev.some((e) => e.sequenceNumber === event.sequenceNumber)) {
            return prev;
          }
          // Insert in order
          const newEvents = [...prev, event].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
          return newEvents;
        });
        setIsLoading(false);
      },
      { replayHistory: false }, // Don't replay history - we already loaded via HTTP
    );
  };

  const unsubscribe = () => {
    if (unsubscribeWs) {
      unsubscribeWs();
      unsubscribeWs = null;
    }
    if (unsubscribeConnectionState) {
      unsubscribeConnectionState();
      unsubscribeConnectionState = null;
    }
    setExecutionId(null);
  };

  const setAgentFilter = (agentId: string | null) => {
    setAgentIdFilter(agentId);
  };

  const clearError = () => {
    setError(null);
  };

  const refreshEvents = async () => {
    const id = executionId();
    if (!id) return;
    setIsLoading(true);
    await loadEventsFromApi(id);
  };

  // Cleanup on unmount
  onCleanup(() => {
    unsubscribe();
  });

  return {
    state: {
      executionId,
      execution,
      events,
      connectionState,
      agentIdFilter,
      isLoading,
      error,
    },
    filteredEvents,
    phase,
    progress,
    planSteps,
    stats,
    agents,
    latestThinking,
    latestMessage,
    subscribe,
    unsubscribe,
    setAgentFilter,
    clearError,
    refreshEvents,
  };
}
